import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type {
    RequiredTranslatable} from "@@/shared/translations/translation";
import {
    requiredTranslatableZodSchema,
} from "@@/shared/translations/translation";
import { generateId } from "@@/utilities/generate-id";
import type {
    MinMaxRange} from "@@/utilities/set-within-range";
import {
    minMaxRangeZodSchema,
} from "@@/utilities/set-within-range";
import type { Except } from "type-fest";
import { z } from "zod";
import type { Optional} from "../optional";
import { optionalZodSchema } from "../optional";
import type {
    OptionalGroupId} from "../optional-group-id";
import {
    optionalGroupIdZodSchema,
} from "../optional-group-id";
import type {
    OptionalPercentage} from "../optional-percentage";
import {
    optionalPercentageZodSchema,
} from "../optional-percentage";

type OptionalGroupAddCommand = {
    readonly _id: string;
    readonly _type: "OPTIONAL_GROUP_ADD_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly providerId: ProviderId;
    readonly optionalGroupId: OptionalGroupId;
    readonly title: RequiredTranslatable;
    readonly optionals: (Optional | OptionalPercentage)[];
    readonly minMax: MinMaxRange;
    readonly free: number;
};

type FactoryParams = Except<
    OptionalGroupAddCommand,
    "_id" | "_type" | "_createdAt"
>;
const optionalGroupFactoryAddCommand = (params: FactoryParams) => {
    const optionalGroupAddCommand: OptionalGroupAddCommand = {
        ...params,
        _id: generateId({ prefix: "optional_group_add_command__" }),
        _type: "OPTIONAL_GROUP_ADD_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return optionalGroupAddCommand;
};

const optionalGroupAddCommandZodSchema: z.ZodSchema<OptionalGroupAddCommand> =
    z.object({
        _id: idZodSchema,
        _type: z.literal("OPTIONAL_GROUP_ADD_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        providerId: providerIdZodSchema,
        optionalGroupId: optionalGroupIdZodSchema,
        title: requiredTranslatableZodSchema,
        minMax: minMaxRangeZodSchema,
        free: z.number(),
        optionals: z.array(optionalZodSchema.or(optionalPercentageZodSchema)),
    });

export { optionalGroupAddCommandZodSchema, optionalGroupFactoryAddCommand };
export type {
    FactoryParams as CreateOptionalGroupAddCommandParams,
    OptionalGroupAddCommand,
};
